import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { verifyPayment } from '../api/payment-api';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Button,
  Chip,
  withStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';

const VerifyPaymentPage = ({ classes }) => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyTransaction = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const id = searchParams.get('id');
        const resourcePath = searchParams.get('resourcePath');

        if (!id || !resourcePath) {
          history.push('/balance');
        }

        const response = await verifyPayment({ id, resourcePath });
        setTransaction(response.data);
        setLoading(false);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('error_something')
        );
        setTransaction(err.response?.data);
        setLoading(false);
      }
    };

    verifyTransaction();
  }, [location.search, t]);

  const handleGoToBalance = () => {
    history.push('/balance');
  };

  const isSuccess = transaction?.callbackParams?.statusCode === 200;

  const renderTransactionDetails = () => {
    if (!transaction?.callbackParams) return null;

    const {
      uuid,
      statusCode,
      statusText,
      transactionId,
      amount,
      currency,
      cardPan,
      cardType,
      cardholderName,
    } = transaction.callbackParams;

    return (
      <Box className={classes.detailsContainer}>
        <Box className={classes.detailRow}>
          <Typography variant="body2" className={classes.detailLabel}>
            {t('transaction_id')}:
          </Typography>
          <Typography variant="body1" className={classes.detailValue}>
            {transactionId}
          </Typography>
        </Box>

        <Box className={classes.detailRow}>
          <Typography variant="body2" className={classes.detailLabel}>
            {t('amount')}:
          </Typography>
          <Typography variant="body1" className={classes.detailValue}>
            {amount} {currency}
          </Typography>
        </Box>

        {cardholderName && (
          <Box className={classes.detailRow}>
            <Typography variant="body2" className={classes.detailLabel}>
              {t('cardholder_name')}:
            </Typography>
            <Typography variant="body1" className={classes.detailValue}>
              {cardholderName}
            </Typography>
          </Box>
        )}

        {cardPan && (
          <Box className={classes.detailRow}>
            <Typography variant="body2" className={classes.detailLabel}>
              {t('card_number')}:
            </Typography>
            <Typography variant="body1" className={classes.detailValue}>
              **** **** **** {cardPan}
            </Typography>
          </Box>
        )}

        {cardType && (
          <Box className={classes.detailRow}>
            <Typography variant="body2" className={classes.detailLabel}>
              {t('card_type')}:
            </Typography>
            <Typography variant="body1" className={classes.detailValue}>
              {cardType}
            </Typography>
          </Box>
        )}

        <Box className={classes.detailRow}>
          <Typography variant="body2" className={classes.detailLabel}>
            {t('status')}:
          </Typography>
          <Chip
            label={statusText}
            className={isSuccess ? classes.successChip : classes.errorChip}
            size="small"
          />
        </Box>

        <Box className={classes.detailRow}>
          <Typography variant="body2" className={classes.detailLabel}>
            {t('reference')}:
          </Typography>
          <Typography
            variant="body1"
            className={classes.detailValue}
            style={{ wordBreak: 'break-word' }}
          >
            {uuid}
          </Typography>
        </Box>
      </Box>
    );
  };
  console.log(transaction);
  return (
    <Container maxWidth="sm" className={classes.container}>
      <Paper elevation={2} className={classes.paper}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress color="primary" />
            <Typography variant="h6" className={classes.loadingText}>
              {t('verifying_payment')}
            </Typography>
          </Box>
        ) : error ? (
          <Box className={classes.errorContainer}>
            <CancelIcon className={classes.errorIcon} />
            <Typography variant="h6" className={classes.errorTitle}>
              {t('verification_error')}
            </Typography>
            {!transaction.callbackParams.statusText && (
              <Typography variant="body1" className={classes.errorMessage}>
                {error}
              </Typography>
            )}
            <Typography variant="body1" className={classes.errorMessage}>
              {transaction.callbackParams.statusText}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              onClick={handleGoToBalance}
            >
              {t('go_to_balance')}
            </Button>
          </Box>
        ) : (
          <Box className={classes.resultContainer}>
            {isSuccess ? (
              <CheckCircleIcon className={classes.successIcon} />
            ) : (
              <CancelIcon className={classes.errorIcon} />
            )}

            <Typography variant="h6" className={classes.resultTitle}>
              {isSuccess ? t('payment_successful') : t('payment_failed')}
            </Typography>

            <Typography variant="body1" className={classes.resultMessage}>
              {transaction.message}
            </Typography>

            <Divider className={classes.divider} />

            <Typography variant="subtitle1" className={classes.detailsTitle}>
              {t('transaction_details')}
            </Typography>

            {renderTransactionDetails()}

            <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              onClick={handleGoToBalance}
            >
              {t('go_to_balance')}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

const styles = (theme) => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 12,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
  loadingText: {
    marginTop: theme.spacing(2),
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  errorTitle: {
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  resultContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  resultTitle: {
    marginBottom: theme.spacing(1),
  },
  resultMessage: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  divider: {
    width: '100%',
    margin: `${theme.spacing(3)}px 0`,
  },
  detailsTitle: {
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  detailsContainer: {
    width: '100%',
    marginBottom: theme.spacing(3),
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing(1)}px 0`,
    borderBottom: '1px solid #eee',
  },
  detailLabel: {
    color: theme.palette.text.secondary,
  },
  detailValue: {
    fontWeight: 500,
  },
  successChip: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#4caf50',
    fontWeight: 500,
  },
  errorChip: {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    color: '#f44336',
    fontWeight: 500,
  },
  actionButton: {
    ...theme.button.filled,
    height: 44,
    marginTop: theme.spacing(2),
  },
});

export default withStyles(styles)(VerifyPaymentPage);
